/**
 * `?modello=` value of the "Modello non in elenco" option. Vehicle slugs always start with the
 * brand slug (`fiat-panda`), so it can't clash with a real model.
 */
export const MODELLO_NON_IN_ELENCO = 'non-in-elenco'

/**
 * The model typed by hand with "Modello non in elenco" (`?modelloLibero=`): single spaces,
 * at most 60 characters. Empty when shorter than 2, so it can't satisfy the required field.
 */
export const pulisciModelloLibero = (value?: string | null) => {
  const testo = (value ?? '').replace(/\s+/g, ' ').trim().slice(0, 60)
  return testo.length >= 2 ? testo : ''
}

/** Length limits of the free search text (`?testo=`). */
export const TESTO_LIBERO_MIN = 2
export const TESTO_LIBERO_MAX = 60

/** The free search text: trimmed, single spaces. Validate it with `erroreTestoLibero`. */
export const pulisciTestoLibero = (value?: string | null) =>
  (value ?? '').replace(/\s+/g, ' ').trim()

/** Why a (cleaned) free search text is refused, `undefined` when it is fine. */
export const erroreTestoLibero = (testo: string) =>
  testo.length < TESTO_LIBERO_MIN
    ? `Scrivi almeno ${TESTO_LIBERO_MIN} caratteri: un nome colore, un codice o un modello.`
    : testo.length > TESTO_LIBERO_MAX
      ? `Scrivi al massimo ${TESTO_LIBERO_MAX} caratteri.`
      : undefined

/** The paint search's state: every choice lives in the URL, so results can be shared. */
export type FiltriRicerca = {
  /** `marche.slug` */
  marca: string
  /** `veicoli.slug`, or `MODELLO_NON_IN_ELENCO` */
  modello?: string
  /** With `MODELLO_NON_IN_ELENCO`: the model typed by the customer. */
  modelloLibero?: string
  /** Paint code, matched exactly (see `cercaTintaPerCodice`). */
  codice?: string
  anno?: number
  /** `FamigliaColore` */
  colore?: string
  /** `tinte.tipo` */
  tipo?: string
}

/** "Cerca nel sito" tab: one text over paints, models, brands and shop products. */
export type FiltriRicercaLibera = {
  /** See `ricercaLibera`. Never sent together with `marca`: the guided search would win. */
  testo: string
  /** 1-based results page. */
  pagina?: number
  /** "Solo corrispondenza esatta": only the paints whose code is exactly `testo`. */
  esatta?: boolean
}

/** Opens /cercatinte on the "Cerca nel sito" tab, with an empty field (header search link). */
export const CERCA_NEL_SITO_HREF = '/cercatinte?testo='

/** URL of the paint search for `filtri`; empty values are left out. */
export const cercaTintaHref = (filtri: FiltriRicerca | FiltriRicercaLibera) => {
  if (!('marca' in filtri)) {
    const params = new URLSearchParams({ testo: filtri.testo })
    if (filtri.esatta) params.set('esatta', '1')
    if (filtri.pagina && filtri.pagina > 1) params.set('pagina', String(filtri.pagina))
    return `/cercatinte?${params}`
  }
  const { marca, modello, modelloLibero, codice, anno, colore, tipo } = filtri
  const params = new URLSearchParams({ marca })
  if (modello) params.set('modello', modello)
  if (modello === MODELLO_NON_IN_ELENCO && modelloLibero) params.set('modelloLibero', modelloLibero)
  if (codice) params.set('codice', codice)
  if (anno) params.set('anno', String(anno))
  if (colore) params.set('colore', colore)
  if (tipo) params.set('tipo', tipo)
  return `/cercatinte?${params}`
}

/**
 * Paint page. The model travels along for the cart line's vehicle: `modello` (`veicoli.slug`),
 * or the typed `modelloLibero` when the model isn't in the list. `codice` is the code the
 * customer searched for: with it the page knows the code is confirmed (`codiceConfermato`).
 */
export const tintaHref = (
  slug: string,
  modello?: string,
  modelloLibero?: string,
  codice?: string,
) => {
  const params = new URLSearchParams()
  if (modello && modello !== MODELLO_NON_IN_ELENCO) params.set('modello', modello)
  else if (modelloLibero) params.set('modelloLibero', modelloLibero)
  if (codice?.trim()) params.set('codice', codice.trim())
  return `/cercatinte/${slug}${params.size ? `?${params}` : ''}`
}
